import fetchApi from '@/lib/tools/api'
import {
	BuildOutlined as BuildIcon,
	BusinessOutlined as BusinessIcon,
	DownloadOutlined as DownloadIcon,
	EmailOutlined as EmailIcon,
	SearchOutlined as SearchIcon,
	StorageOutlined as StorageIcon,
	TerminalOutlined as TerminalIcon
} from '@mui/icons-material'
import {Alert, Box, Card, CardContent, CardHeader, CircularProgress, Grid, TextField, Typography} from '@mui/material'
import React, {useCallback, useEffect, useState} from 'react'
import {Title, useNotify} from 'react-admin'
import ScriptCategory from './components/ScriptCategory'

export interface ScriptParameter {
	name: string
	type: 'text' | 'number' | 'boolean' | 'select' | 'email'
	label: string
	description?: string
	required?: boolean
	defaultValue?: any
	options?: { value: any; label: string }[]
	// Backend-specific fields that get transformed
	choices?: Record<string, string>
	default?: any
}

export interface Script {
	id: string
	name: string
	description: string
	category: string
	parameters: ScriptParameter[]
	dangerous?: boolean
	longRunning?: boolean
}

export interface ScriptCategory {
	id: string
	name: string
	icon: React.ReactElement
	scripts: Script[]
}

interface ApiScriptCategory {
	label: string
	icon: string
	scripts: Record<string, Script>
}

interface ScriptsListResponse {
	scripts: Record<string, ApiScriptCategory>
	categories: string[]
}

const categoryIcons = {
	sirene: <BusinessIcon/>,
	brevo: <EmailIcon/>,
	maintenance: <BuildIcon/>,
	exports: <DownloadIcon/>,
	database: <StorageIcon/>
}

const Scripts = () => {
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [categories, setCategories] = useState<ScriptCategory[]>([])
	const [searchTerm, setSearchTerm] = useState('')
	const [filteredCategories, setFilteredCategories] = useState<ScriptCategory[]>([])

	const notify = useNotify()

	const fetchScripts = useCallback(async () => {
		setLoading(true)
		setError(null)

		try {
			const response = await fetchApi<ScriptsListResponse>('/scripts/list')

			// Add safety checks for the API response
			if (!response || !response.scripts || typeof response.scripts !== 'object') {
				throw new Error('Invalid API response: missing or malformed scripts data')
			}

			// Process the scripts object to build categories array
			const processedCategories: ScriptCategory[] = Object.entries(response.scripts || {}).map(([categoryId, apiCategory]) => {
				// Add safety checks for apiCategory and its scripts
				if (!apiCategory || typeof apiCategory !== 'object') {
					console.warn(`Invalid category data for ${categoryId}:`, apiCategory)
					return {
						id: categoryId,
						name: categoryId,
						icon: categoryIcons[categoryId as keyof typeof categoryIcons] || <TerminalIcon/>,
						scripts: []
					}
				}

				// Convert scripts object to array with safety checks
				const scriptsObject = apiCategory.scripts || {}
				const scriptsArray: Script[] = Object.entries(scriptsObject).map(([scriptId, script]) => {
					if (!script || typeof script !== 'object') {
						console.warn(`Invalid script data for ${scriptId}:`, script)
						return {
							id: scriptId,
							name: scriptId,
							description: '',
							category: categoryId,
							parameters: []
						}
					}

					return {
						...script,
						id: scriptId,
						category: categoryId,
						parameters: (script.parameters || []).map((param: any) => {
							// Transform choices from backend format to frontend format
							if (param.type === 'select' && param.choices) {
								const options = Object.entries(param.choices).map(([value, label]) => ({
									value,
									label: label as string
								}))
								return {
									...param,
									options,
									// Keep defaultValue if it exists, otherwise use default
									defaultValue: param.defaultValue !== undefined ? param.defaultValue : param.default
								}
							}
							// For non-select parameters, keep defaultValue if exists, otherwise use default
							return {
								...param,
								defaultValue: param.defaultValue !== undefined ? param.defaultValue : param.default
							}
						})
					}
				})

				return {
					id: categoryId,
					name: apiCategory.label || categoryId,
					icon: categoryIcons[categoryId as keyof typeof categoryIcons] || <TerminalIcon/>,
					scripts: scriptsArray
				}
			})

			setCategories(processedCategories || [])
			setFilteredCategories(processedCategories || [])
		} catch (err: any) {
			const errorMessage = err.message || 'Erreur lors du chargement des scripts'
			console.error('Error fetching scripts:', err)
			setError(errorMessage)
			notify(`Erreur: ${errorMessage}`, {type: 'error'})
			// Set empty arrays as fallback
			setCategories([])
			setFilteredCategories([])
		} finally {
			setLoading(false)
		}
	}, [notify])

	useEffect(() => {
		fetchScripts()
	}, [fetchScripts])

	// Filter categories and scripts based on search term
	useEffect(() => {
		if (!searchTerm.trim()) {
			setFilteredCategories(categories || [])
			return
		}

		// Add safety checks for categories array
		if (!Array.isArray(categories)) {
			console.warn('Categories is not an array:', categories)
			setFilteredCategories([])
			return
		}

		const filtered = categories.map(category => {
			// Add safety checks for category and its scripts
			if (!category || !Array.isArray(category.scripts)) {
				console.warn('Invalid category or scripts array:', category)
				return {
					...category,
					scripts: []
				}
			}

			return {
				...category,
				scripts: category.scripts.filter(script => {
					// Add safety checks for script properties
					if (!script) return false

					const name = script.name || ''
					const description = script.description || ''
					const searchTermLower = searchTerm.toLowerCase()

					return name.toLowerCase().includes(searchTermLower) ||
						description.toLowerCase().includes(searchTermLower)
				})
			}
		}).filter(category => {
			// Ensure category exists and has scripts
			return category && Array.isArray(category.scripts) && category.scripts.length > 0
		})

		setFilteredCategories(filtered || [])
	}, [searchTerm, categories])

	return (
		<Box sx={{padding: 3, maxWidth: '100%', margin: '0 auto'}}>
			<Title title="Scripts de maintenance"/>

			<Card sx={{mb: 3}}>
				<CardHeader
					title={
						<Box display="flex" alignItems="center" gap={1}>
							<TerminalIcon color="primary"/>
							<Typography variant="h5">
								Scripts de maintenance
							</Typography>
						</Box>
					}
					subheader="Interface d'exécution des scripts de maintenance, synchronisation et export"
				/>

				<CardContent>
					{error && (
						<Alert severity="error" sx={{mb: 3}}>
							<Typography variant="body1" fontWeight="bold">
								Erreur de chargement
							</Typography>
							<Typography variant="body2">
								{error}
							</Typography>
						</Alert>
					)}

					{loading ? (
						<Box display="flex" alignItems="center" justifyContent="center" py={4}>
							<CircularProgress size={40}/>
							<Typography variant="body1" sx={{ml: 2}}>
								Chargement des scripts...
							</Typography>
						</Box>
					) : (
						<>
							{/* Search Field */}
							<Box sx={{mb: 3}}>
								<TextField
									fullWidth
									placeholder="Rechercher un script..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									InputProps={{
										startAdornment: <SearchIcon sx={{mr: 1, color: 'text.secondary'}}/>
									}}
								/>
							</Box>

							{/* Categories */}
							{Array.isArray(filteredCategories) && filteredCategories.length > 0 ? (
								<Grid container spacing={3}>
									{filteredCategories.map((category) => {
										// Add safety check for each category
										if (!category || !category.id) {
											console.warn('Invalid category in render:', category)
											return null
										}

										return (
											<Grid item xs={12} key={category.id}>
												<ScriptCategory category={category}/>
											</Grid>
										)
									})}
								</Grid>
							) : (
								<Box textAlign="center" py={4}>
									<Typography variant="h6" color="text.secondary">
										{searchTerm ? 'Aucun script trouvé' : 'Aucun script disponible'}
									</Typography>
									{searchTerm && (
										<Typography variant="body2" color="text.secondary">
											Essayez de modifier vos critères de recherche
										</Typography>
									)}
								</Box>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</Box>
	)
}

export default Scripts
